import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import { useAuth } from '@/auth/authContext';
import { useGuardedExit } from '@/components/confirmDialog/unsavedContext';
import { useLogoutConfirm } from '@/components/confirmDialog/useLogoutConfirm';
import { tr } from '@/i18n/tr';
import styles from './userMenu.module.scss';

/**
 * Header avatar. Guest → /login (as the template did); signed-in → the user's
 * initial with a minimal menu. TEMPORARY surface: the profile module chat
 * replaces the menu with the real profile entry.
 */
export function UserMenu() {
  const { status, user, logout } = useAuth();
  const navigate = useNavigate();
  const confirmLogout = useLogoutConfirm();
  const guardExit = useGuardedExit();
  const [open, setOpen] = useState(false);

  if (status !== 'authed' || !user) {
    return (
      <button
        type="button"
        className={styles.trigger}
        aria-label={tr.auth.loginLink}
        onClick={() => navigate('/login')}
      >
        <User size={18} />
      </button>
    );
  }

  // The menu closes only after a confirmed logout — cancelling leaves it open
  // where the user left it, rather than dismissing under them.
  const signOut = async () => {
    if (!(await confirmLogout())) return;
    setOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={`${styles.trigger} ${styles.initial}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user.name}
        onClick={() => setOpen((o) => !o)}
      >
        {user.name.trim().charAt(0).toLocaleUpperCase('tr')}
      </button>

      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <div className={styles.menu} role="menu">
            <p className={styles.name}>{user.name}</p>
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={async () => {
                if (!(await guardExit())) return;
                setOpen(false);
                navigate('/profile');
              }}
            >
              {tr.auth.menu.profile}
            </button>
            <button
              type="button"
              role="menuitem"
              className={`${styles.item} ${styles.danger}`}
              onClick={signOut}
            >
              {tr.auth.menu.logout}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
